/**
 * The dashboard's table.
 *
 * One implementation for every section, so a list of applications and a list of
 * articles cannot drift into two different ideas of what a row is. Columns say
 * what to render and whether the cell reads down the right-hand edge; the
 * header row is stated once.
 */
export interface Column<T> {
  key: string;
  header: string;
  /** Numbers, dates and actions line up down the right. */
  align?: 'right';
  render: (row: T) => React.ReactNode;
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  label,
  testId,
  empty,
}: {
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  /** Names the table for a screen reader — and for a test. */
  label: string;
  testId?: string;
  /** What to show instead of the table when there is nothing to list. */
  empty?: React.ReactNode;
}): React.JSX.Element {
  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <table className="table" aria-label={label} data-testid={testId}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={column.align === 'right' ? 'table__right' : undefined}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={rowKey(row)}>
            {columns.map((column) => (
              <td key={column.key} className={column.align === 'right' ? 'table__right' : undefined}>
                {column.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
